import axios from 'axios'
import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from './api'

import { getUser } from './oneUser'
import calculateAge from '../src/fonctions/calculateAge'
import CookieManager from '../src/lib/CookiesManager'

interface UserData {
  user: {
    birthday: string
  }
}

const postUserProfile = async (profileData: any, isNewProfile: boolean) => {
  const userId = CookieManager.getUserId()
  const token = CookieManager.getToken()

  if (!userId || !token) {
    throw new Error('User ID or token is missing')
  }

  try {
    const userData: UserData = await getUser(userId, token)

    if (!userData.user.birthday) {
      throw new Error('User birthday is missing')
    }

    const age = calculateAge(userData.user.birthday)

    const completeProfileData = {
      ...profileData,
      age,
    }

    const response = await axios({
      method: isNewProfile ? 'post' : 'patch',
      url: `${API_BASE_URL}/user/${userId}/profile`,
      data: completeProfileData,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })

    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        feature: 'userProfile',
        operation: isNewProfile ? 'create' : 'update',
      },
      extra: {
        userId,
        profileData,
        isNewProfile,
        response: axios.isAxiosError(error) ? error.response?.data : null,
      },
    })

    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Failed to ${
          isNewProfile ? 'create' : 'update'
        } profile: ${JSON.stringify(error.response.data)}`,
      )
    }

    throw error
  }
}

const getUserProfile = async () => {
  const userId = CookieManager.getUserId()
  const token = CookieManager.getToken()

  if (!userId || !token) {
    throw new Error('User ID or token is missing')
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/user/${userId}/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'userProfile', operation: 'fetch' },
      extra: { userId },
    })

    if (axios.isAxiosError(error)) {
      if (error.response && error.response.status === 422) {
        return null
      }
    }
    throw error
  }
}

const getLanguages = async (token: string) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/languages`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    const languagesFromBackend = response.data
    const formattedLanguages = languagesFromBackend
      .replace(/{/g, '[')
      .replace(/}/g, ']')
    const languagesArray = JSON.parse(formattedLanguages)

    localStorage.setItem('languages', JSON.stringify(languagesArray))

    return languagesArray
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'languages', operation: 'fetch' },
      extra: { token },
    })

    throw error
  }
}

const fetchAllProfiles = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/profiles`, {
      headers: {
        'x-api-key': X_API_KEY,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch profiles')
    }

    const profiles = await response.json()
    return profiles
  } catch (error) {
    console.error('❌ Erreur fetchUserProfiles:', error)
    return []
  }
}

export const getUserGender = async (userId: number) => {
  try {
    const profiles = await fetchAllProfiles()
    const userProfile = profiles.find(
      (profile: any) => profile.userId === userId,
    )

    return userProfile?.gender || 'unknown'
  } catch (error) {
    console.error('❌ Erreur getUserGender:', error)
    return 'unknown'
  }
}

export { postUserProfile, getUserProfile, getLanguages, fetchAllProfiles }
